import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { Hero } from './Hero.jsx';
import { useMotion } from '../../hooks/useMotion.js';
import { reducedMotion } from '../../hooks/useLenis.js';
import './Home.css';

const capabilities = [
  {
    num: '01',
    tag: 'Core Focus',
    title: 'Customized Strategies',
    short: 'Tailored solutions to meet your unique objectives.',
    desc: "Every portfolio is engineered from the ground up around your family's distinct liquidity needs, risk tolerance, and multi-generational vision. No off-the-shelf products or rigid formulas.",
    image: '/media/concept-finance-investment-profit-growth-management-financial-graphical.webp',
    tags: ['Bespoke Portfolios', 'Risk Architecture', 'Tailored Allocation'],
  },
  {
    num: '02',
    tag: 'Expansion',
    title: 'Growth Acceleration',
    short: 'Driving business growth through innovative approaches.',
    desc: 'Active capital deployment across global markets, structured solutions, and private asset opportunities designed to capture upside while preserving capital through market cycles.',
    image: '/media/financial-growth-investment-success-with-stacks-gold-coins.webp',
    tags: ['Capital Preservation', 'Global Markets', 'Alpha Generation'],
  },
  {
    num: '03',
    tag: 'Advisory',
    title: 'Expert Support',
    short: 'Providing the expertise needed for success.',
    desc: 'Direct access to veteran MAS-licensed portfolio managers and cross-border structuring specialists who navigate international regulatory regimes alongside premier global custodians.',
    image: '/media/corporate-professional-project-talking-accountant-workplace.webp',
    tags: ['MAS-Licensed Advice', 'Direct PM Access', 'Custody Coordination'],
  },
  {
    num: '04',
    tag: 'Commitment',
    title: 'Long-Term Partnership',
    short: 'Building a collaborative relationship for sustained success.',
    desc: 'We sit firmly on your side of the table. With complete fee transparency, independent custody, and fiduciary alignment, our commitment extends across generations.',
    image: '/media/businessmen-discussing-plans-with-small-wooden-hou-2026-09-21-18-34-17-utc.webp',
    tags: ['Fiduciary Alignment', 'Zero Conflict of Interest', 'Generational Wealth'],
  },
];

export const Home = () => {
  useMotion();
  const bannerRef = useRef(null);
  const bannerMaskRef = useRef(null);
  const bannerImgRef = useRef(null);

  useEffect(() => {
    document.title = 'Home | ASTRID FIRST Wealth Advisory';
  }, []);

  useEffect(() => {
    if (reducedMotion()) return;

    const banner = bannerRef.current;
    const mask = bannerMaskRef.current;
    const img = bannerImgRef.current;
    if (!banner || !mask) return;

    const mm = gsap.matchMedia();

    // The zoom scales the mask layer rather than tweening mask-size: a mask scales with
    // its element, so the result is identical, but transform stays on the compositor
    // while mask-size repaints the whole full-width banner every frame.
    const zoom = (tl, startPx, endPx) => {
      gsap.set(mask, { WebkitMaskSize: `${startPx()}px auto`, maskSize: `${startPx()}px auto` });
      tl.fromTo(
        mask,
        { scale: 1, opacity: 0.5 },
        {
          scale: () => endPx / startPx(),
          opacity: 0,
          duration: 1,
          ease: 'power2.inOut',
        },
        0
      );
    };

    // Desktop and Tablet (>= 768px): smooth scrubbed mask zoom
    mm.add('(min-width: 768px)', () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: banner,
          start: 'top 85%',
          end: 'bottom 15%',
          scrub: 1,
          invalidateOnRefresh: true,
        },
      });

      // Mirrors the CSS clamp(200px, 22vw, 320px).
      zoom(tl, () => Math.min(320, Math.max(200, window.innerWidth * 0.22)), 5500);

      if (img) {
        tl.fromTo(
          img,
          { scale: 1.06 },
          { scale: 1, duration: 1, ease: 'none' },
          0
        );
      }
    });

    // Mobile (< 768px)
    mm.add('(max-width: 767px)', () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: banner,
          start: 'top 90%',
          end: 'bottom 20%',
          scrub: 1,
        },
      });

      zoom(tl, () => 160, 3600);
    });

    return () => {
      mm.revert();
    };
  }, []);

  const toggleCardSplit = (e) => {
    if (e.target.closest('a')) return;
    const card = e.currentTarget;
    const isSplit = card.classList.toggle('is-split');
    card.setAttribute('aria-expanded', isSplit ? 'true' : 'false');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleCardSplit(e);
    }
  };

  return (
    <>
      <Hero />

      <div className="marquee" aria-hidden="true">
        <div className="marquee-run">
          <span>Independent asset manager</span>
          <span>MAS-licensed since 2009</span>
          <span>Multi Family Office platform</span>
          <span>Bespoke solutions</span>
          <span>Transparent pricing</span>
          <span>Singapore</span>
        </div>
        <div className="marquee-run">
          <span>Independent asset manager</span>
          <span>MAS-licensed since 2009</span>
          <span>Multi Family Office platform</span>
          <span>Bespoke solutions</span>
          <span>Transparent pricing</span>
          <span>Singapore</span>
        </div>
      </div>

      <div className="spine">
        <section className="section shell" id="about">
          <div className="track">
            <div className="track-head track-head--sticky">
              <p className="eyebrow">Since 2009</p>
              <h2 data-reveal="lines">Independent Asset Manager</h2>
            </div>
            <div className="prose">
              <p className="lead" data-reveal="fade">
                Founded in 2009, ASTRID FIRST Wealth Advisory is an independent
                asset manager offering clients an alternative to the traditional
                private banking model.
              </p>
              <p data-reveal="fade">
                We prioritise responsiveness and alignment with your financial
                objectives, providing bespoke solutions tailored to your needs.
                Our clients benefit from transparent and competitive pricing,
                multi-platform solutions for flexible management, and
                independent advice free from conflicts of interest.
              </p>
              <p data-reveal="fade">
                We are committed to delivering customised wealth management
                strategies that foster trust and long-lasting partnerships.
              </p>
              <p style={{ marginTop: '2rem' }}>
                <a className="btn btn--bare" href="/introduction">
                  Read our introduction
                </a>
              </p>
            </div>
          </div>
        </section>
      </div>

      <section
        className="home-photo-banner"
        aria-label="Singapore Skyline Panorama"
        ref={bannerRef}
      >
        <div className="home-photo-banner-wrap">
          <img
            src="/media/landscape-view-of-singapore-business-district-and-2026-03-17-00-04-08-utc.webp"
            alt="Singapore Business District and Financial Skyline"
            loading="lazy"
            className="home-photo-banner-img"
            ref={bannerImgRef}
          />
          <div
            className="home-photo-mask-layer"
            ref={bannerMaskRef}
            aria-hidden="true"
          />
        </div>
      </section>

      <section className="section" id="how-we-work" aria-labelledby="work-title">
        <div className="shell" style={{ marginBottom: 'clamp(2rem, 5vw, 3.5rem)' }}>
          <p className="eyebrow">How we work</p>
          <h2 id="work-title" data-reveal="lines">
            Prioritizing Your Business Goals
          </h2>
          <p
            className="lead"
            data-reveal="fade"
            style={{ maxWidth: '44rem', marginTop: '1.2rem' }}
          >
            We align with your vision and provide the expert support behind
            sustained results: customised strategies, accelerated growth,
            sharper efficiency and maximised profit.
          </p>
        </div>

        <div className="work-viewport" data-horizontal>
          <div className="work-track" data-horizontal-track>
            {capabilities.map((cap) => (
              <article
                key={cap.num}
                className="work-card"
                data-split-card
                tabIndex={0}
                role="button"
                aria-expanded="false"
                onClick={toggleCardSplit}
                onKeyDown={handleKeyDown}
                title={`${cap.title} — click to reveal details`}
              >
                {/* Background Layer: Detailed Explanation */}
                <div className="work-card-back">
                  <div className="work-back-header">
                    <span className="work-card-num">{cap.num}</span>
                    <span className="work-back-pill">Capability</span>
                  </div>
                  <div className="work-back-body">
                    <h3 className="work-back-title">{cap.title}</h3>
                    <p className="work-back-lead">{cap.short}</p>
                    <p className="work-back-desc">{cap.desc}</p>
                    <div className="work-back-tags">
                      {cap.tags.map((t) => (
                        <span key={t}>{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="work-back-foot">
                    <a
                      className="btn btn--bare"
                      href="/business-model"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Learn more about our model &rarr;
                    </a>
                  </div>
                </div>

                {/* Front Layer: Split Top (Title) & Split Bottom (Photo) */}
                <div className="work-card-front" aria-hidden="true">
                  <div className="work-split-top">
                    <div className="work-split-top-content">
                      <div className="work-card-meta">
                        <span className="work-card-num">{cap.num}</span>
                        <span className="work-card-tag">{cap.tag}</span>
                      </div>
                      <h3 className="work-front-title">{cap.title}</h3>
                    </div>
                    <div className="work-split-seam-top">
                      <span>Scroll to uncover</span>
                      <span>&darr; &uarr;</span>
                    </div>
                  </div>

                  <div className="work-split-bottom">
                    <div className="work-split-photo-wrap">
                      <img
                        src={cap.image}
                        alt={cap.title}
                        loading="lazy"
                        className="work-split-img"
                      />
                      <div className="work-split-photo-overlay" />
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="shell" style={{ marginTop: '2.5rem' }}>
          <p>
            <a className="btn btn--bare" href="/business-model">
              Learn more about our model
            </a>
          </p>
        </div>
      </section>
    </>
  );
};